<template>
  <div class="login-page">
    <div class="login-card">
      <div class="brand-section">
        <img class="brand-logo" src="../../../assets/image/logo.png" alt="logo">
        <div class="brand-copy">
          <h1>在线学习管理系统</h1>
          <p>支持 BSG 平台免密进入，也保留本地账号密码登录。</p>
        </div>
      </div>

      <div class="status-row">
        <div v-if="ssoLoading" class="status-box status-loading">
          <i class="el-icon-loading"></i>
          <span>检测到 SSO 票据，正在登录管理端...</span>
        </div>
        <div v-else-if="ssoError" class="status-box status-error">
          <i class="el-icon-warning-outline"></i>
          <span>{{ ssoError }}</span>
        </div>
        <div v-else class="status-box status-info">
          <i class="el-icon-s-promotion"></i>
          <span>未检测到 SSO 票据时，可直接使用本地账号密码登录。</span>
        </div>
        <el-button
          class="status-retry"
          circle
          plain
          icon="el-icon-loading"
          :disabled="ssoLoading"
          @click="retrySsoLogin"
          title="重新检测 SSO"
        ></el-button>
      </div>

      <div class="login-input">
        <div class="input-item">
          <el-input
            prefix-icon="el-icon-user"
            v-model.trim="username"
            placeholder="请输入用户名"
            :disabled="loginLoading || ssoLoading"
            @keyup.enter.native="handlePasswordFocus"
          ></el-input>
        </div>
        <div class="input-item">
          <el-input
            ref="passwordInput"
            prefix-icon="el-icon-lock"
            placeholder="请输入密码"
            v-model="password"
            :disabled="loginLoading || ssoLoading"
            show-password
            @keyup.enter.native="handlePasswordLogin"
          ></el-input>
        </div>
      </div>

      <el-button
        class="login-btn"
        type="primary"
        :loading="loginLoading"
        :disabled="loginLoading || ssoLoading"
        @click="handlePasswordLogin"
      >
        登陆
      </el-button>
    </div>
  </div>
</template>

<script>
import { exchangeSsoToken, getUser, login } from '../../../api/api'
import { setLock } from '@/utils/lock'

export default {
  data() {
    return {
      username: '',
      password: '',
      loginLoading: false,
      ssoLoading: false,
      ssoError: '',
      ssoToken: ''
    }
  },
  created() {
    this.trySsoLogin()
  },
  methods: {
    getRedirectPath() {
      return this.$route.query.redirect || '/'
    },
    getSsoToken() {
      if (this.$route.query && this.$route.query.sso_token) {
        return this.$route.query.sso_token
      }
      return new URLSearchParams(window.location.search || '').get('sso_token') || ''
    },
    stripTransientQuery() {
      const url = new URL(window.location.href)
      url.searchParams.delete('sso_token')
      url.searchParams.delete('redirect')
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
    },
    handlePasswordFocus() {
      this.$refs.passwordInput.focus()
    },
    retrySsoLogin() {
      this.trySsoLogin()
    },
    async trySsoLogin() {
      this.ssoToken = this.getSsoToken()
      this.ssoError = ''

      if (!this.ssoToken) {
        return
      }

      this.ssoLoading = true
      try {
        const res = await exchangeSsoToken({
          ssoToken: this.ssoToken,
          clientType: 'admin'
        })

        if (res.code === 1000 && res.data && res.data.token) {
          await this.finishLogin(res.data.token, '平台快捷登录成功，正在跳转...')
          return
        }

        this.ssoError = res.message || '管理端 SSO 登录失败'
      } catch (err) {
        this.ssoError = '管理端 SSO 请求失败，请稍后重试'
      } finally {
        this.ssoLoading = false
      }
    },
    async handlePasswordLogin() {
      if (this.loginLoading) {
        return
      }
      if (!this.username) {
        this.$message({
          message: '请输入用户名',
          type: 'warning'
        })
        return
      }
      if (!this.password) {
        this.$message({
          message: '请输入密码',
          type: 'warning'
        })
        return
      }

      this.loginLoading = true
      try {
        const res = await login({
          username: this.username,
          password: this.password
        })
        if (res.code === 1000 && res.data && res.data.token) {
          await this.finishLogin(res.data.token, '登陆成功，正在跳转...')
          return
        }
        this.$message.error(res.message || '登陆失败')
      } catch (err) {
        console.error('login error:', err)
        this.$message.error('登录请求失败，请稍后重试')
      } finally {
        this.loginLoading = false
      }
    },
    async finishLogin(token, message) {
      this.$message({
        message,
        type: 'success'
      })
      this.$store.commit('user/setToken', token)
      await this.getUserInfo()
      setLock(false)
      this.stripTransientQuery()
      this.$router.push(this.getRedirectPath()).catch(() => {})
    },
    getUserInfo() {
      return getUser().then(res => {
        if (res.code == 1000) {
          this.$store.commit('user/setUser', JSON.stringify(res.data))
        }
      })
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(circle at top, rgba(15, 118, 110, 0.08), transparent 28%),
    linear-gradient(180deg, #f7fafc 0%, #eef2f6 100%);
}

.login-card {
  width: 100%;
  max-width: 460px;
  padding: 32px 34px 30px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e6edf5;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.brand-logo {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.brand-copy h1 {
  margin: 0;
  color: #0f172a;
  font-size: 28px;
  line-height: 1.2;
}

.brand-copy p {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-box {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
}

.status-loading {
  background: #eef6ff;
  color: #2563eb;
}

.status-error {
  background: #fff1f2;
  color: #dc2626;
}

.status-info {
  background: #f8fafc;
  color: #475569;
}

.status-retry {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  color: #409eff;
  border-color: #dbeafe;
  background: #ffffff;
}

.login-input {
  width: 100%;
  margin-top: 8px;
}

.input-item {
  margin-top: 16px;
}

.el-input ::v-deep .el-input__inner {
  height: 46px;
  border-radius: 10px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  font-weight: 500;
}

.el-input ::v-deep .el-input__inner:focus {
  border-color: #3b82f6;
  background-color: #ffffff;
}

.login-btn {
  width: 100%;
  height: 46px;
  margin-top: 24px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 640px) {
  .login-page {
    padding: 16px;
  }

  .login-card {
    padding: 24px 20px;
  }

  .brand-section {
    align-items: flex-start;
  }

  .brand-copy h1 {
    font-size: 24px;
  }

  .status-row {
    align-items: flex-start;
  }
}
</style>
